/**
 * MINI GERENCIAMENTO — TURMAS.
 *
 * A turma é da EQUIPE (decisão do usuário) e o aluno pode estar em VÁRIAS
 * turmas. Os alunos são escolhidos da lista que já existe (nada de cadastro
 * paralelo) e cada um pode ter um valor de mensalidade próprio; quem não tem
 * usa o valor padrão da turma.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dica, BarraAcoes } from '../common/Formulario';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Search, Trash2, Users } from 'lucide-react';
import { minhaEquipeApi } from '../../lib/api';
import {
  DIAS_SEMANA, diasTurma, formatarValor, horarioTurma, valorDoAluno,
} from '../../lib/minhaEquipe';

const FORM_VAZIO = {
  name: '', team_id: '', weekdays: [], start_time: '', end_time: '', level: '',
  default_amount: '', due_day: 10, notes: '', active: true,
  professor_ids: [], students: [],
};

export default function TurmasEquipe({ turmas = [], onMudou }) {
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [alunos, setAlunos] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);
  // As MESMAS validações de antes (mesmas condições e mesmas mensagens) agora
  // aparecem JUNTO ao campo, em vez de num aviso no canto.
  const [erros, setErros] = useState({});

  useEffect(() => {
    minhaEquipeApi.alunos()
      .then((r) => setAlunos(r.data || []))
      .catch(() => setAlunos([]));
    // A turma é SEMPRE de uma equipe: carrega as equipes reais do usuário e, se
    // ele responde por uma só, já vem escolhida (era o bug da turma sem equipe).
    minhaEquipeApi.equipes()
      .then((r) => {
        const lista = r.data || [];
        setEquipes(lista);
        setForm((prev) => (prev.team_id || lista.length !== 1 ? prev : { ...prev, team_id: lista[0].id }));
      })
      .catch(() => setEquipes([]));
  }, []);

  // ⚠️ Professores: SÓ os da equipe. A lista segue a equipe escolhida na turma
  // (um professor de outra equipe não aparece — e não vaza nome/e-mail alheio).
  useEffect(() => {
    let ativo = true;
    minhaEquipeApi.professores(form.team_id ? { team_id: form.team_id } : {})
      .then((r) => { if (ativo) setProfessores(r.data || []); })
      .catch(() => { if (ativo) setProfessores([]); });
    return () => { ativo = false; };
  }, [form.team_id]);

  // O select precisa das equipes já carregadas para abrir uma turma existente.
  useEffect(() => {
    if (equipes.length === 1) {
      setForm((prev) => (prev.team_id ? prev : { ...prev, team_id: equipes[0].id }));
    }
  }, [equipes]);

  const nomeDaEquipe = (teamId) => equipes.find((e) => e.id === teamId)?.name || '';

  const alunosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return alunos;
    return alunos.filter((a) => (a.name || '').toLowerCase().includes(termo));
  }, [alunos, busca]);

  const abrirNova = () => {
    setEditando(null);
    setForm({ ...FORM_VAZIO, team_id: equipes.length === 1 ? equipes[0].id : '' });
    setBusca('');
    setErros({});
    setAberto(true);
  };

  const abrirEdicao = (turma) => {
    setEditando(turma);
    setForm({
      name: turma.name || '',
      team_id: turma.team_id || (equipes.length === 1 ? equipes[0].id : ''),
      weekdays: turma.weekdays || [],
      start_time: turma.start_time || '',
      end_time: turma.end_time || '',
      level: turma.level || '',
      default_amount: turma.default_amount ?? '',
      due_day: turma.due_day || 10,
      notes: turma.notes || '',
      active: turma.active !== false,
      professor_ids: turma.professor_ids || [],
      students: (turma.students || []).map((s) => ({
        athlete_id: s.athlete_id, amount: s.amount ?? '',
      })),
    });
    setBusca('');
    setErros({});
    setAberto(true);
  };

  const alternarAluno = (athleteId) => {
    setForm((prev) => {
      const existe = prev.students.some((s) => s.athlete_id === athleteId);
      return {
        ...prev,
        students: existe
          ? prev.students.filter((s) => s.athlete_id !== athleteId)
          : [...prev.students, { athlete_id: athleteId, amount: '' }],
      };
    });
  };

  const mudarValorAluno = (athleteId, valor) => {
    setForm((prev) => ({
      ...prev,
      students: prev.students.map((s) => (
        s.athlete_id === athleteId ? { ...s, amount: valor } : s
      )),
    }));
  };

  const salvar = async () => {
    if (!form.name.trim()) { setErros((prev) => ({ ...prev, name: 'Informe o nome da turma' })); return; }
    if (!form.team_id) { setErros((prev) => ({ ...prev, team_id: 'Escolha a equipe da turma' })); return; }
    setSalvando(true);
    try {
      const dados = {
        name: form.name,
        team_id: form.team_id,
        weekdays: form.weekdays,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        level: form.level || null,
        default_amount: form.default_amount === '' ? null : Number(form.default_amount),
        due_day: Number(form.due_day) || 10,
        notes: form.notes || null,
        active: form.active,
        professor_ids: form.professor_ids,
        students: form.students.map((s) => ({
          athlete_id: s.athlete_id,
          amount: s.amount === '' || s.amount === null ? null : Number(s.amount),
        })),
      };
      if (editando) {
        await minhaEquipeApi.atualizarTurma(editando.id, dados);
        toast.success('Turma atualizada');
      } else {
        await minhaEquipeApi.criarTurma(dados);
        toast.success('Turma criada');
      }
      setErros({});
      setAberto(false);
      if (onMudou) onMudou();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar a turma');
    } finally {
      setSalvando(false);
    }
  };

  const apagar = async (turma) => {
    if (!window.confirm(
      `Apagar a turma "${turma.name}"? A chamada e as mensalidades já lançadas continuam guardadas.`,
    )) return;
    try {
      const { data } = await minhaEquipeApi.apagarTurma(turma.id);
      toast.success(`Turma apagada (${data.chamadas_mantidas} chamada(s) e ${data.mensalidades_mantidas} mensalidade(s) mantidas)`);
      if (onMudou) onMudou();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar a turma');
    }
  };

  return (
    <div className="space-y-4" data-testid="turmas-equipe">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[#A1A1AA]">
          {turmas.length} turma(s). A turma é da equipe e o aluno pode estar em várias.
        </p>
        <Button onClick={abrirNova} data-testid="nova-turma" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
          <Plus size={16} className="mr-1" /> Nova turma
        </Button>
      </div>

      {turmas.length === 0 ? (
        <p className="text-sm text-[#71717A]" data-testid="turmas-vazio">
          Nenhuma turma ainda. Crie a primeira (ex.: Infantil, Adulto Iniciante, Competição).
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {turmas.map((t) => (
            <div key={t.id} data-testid={`turma-${t.id}`}
                 className="rounded-lg border border-[#27272A] bg-[#141414] p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-white font-semibold">{t.name}</p>
                  <p className="text-micro text-[#71717A]">
                    {[nomeDaEquipe(t.team_id) || 'sem equipe', diasTurma(t), horarioTurma(t), t.level]
                      .filter(Boolean).join(' · ') || 'sem horário definido'}
                  </p>
                </div>
                <div className="flex gap-1">
                  {/* Os botões vêm do que o SERVIDOR autorizou (a tela não decide regra):
                      editar = criador | responsável pela equipe | professor atribuído;
                      excluir = criador | responsável pela equipe. */}
                  {t.pode_editar !== false && (
                    <Button size="sm" variant="outline" onClick={() => abrirEdicao(t)}
                            data-testid={`editar-turma-${t.id}`}
                            className="border-[#27272A] text-[#A1A1AA] hover:text-white"><Pencil size={16} /></Button>
                  )}
                  {t.pode_apagar !== false && (
                    <Button size="sm" variant="outline" onClick={() => apagar(t)}
                            data-testid={`apagar-turma-${t.id}`}
                            className="border-[#27272A] text-red-400 hover:text-red-300"><Trash2 size={16} /></Button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-micro text-[#A1A1AA]">
                <span className="flex items-center gap-1"><Users size={12} /> {t.total_alunos} aluno(s)</span>
                <span>Mensalidade: {formatarValor(t.default_amount)}</span>
                <span>Vence dia {t.due_day}</span>
                {t.active === false && <span className="text-amber-400">inativa</span>}
              </div>
              {t.students?.length > 0 && (
                <p className="text-micro text-[#71717A] truncate">
                  {t.students.slice(0, 6).map((s) => s.name).join(', ')}
                  {t.students.length > 6 ? ` +${t.students.length - 6}` : ''}
                </p>
              )}
              {/* Quem não pode tudo precisa saber o que pode (e por quê). */}
              {t.pode_editar === false && (
                <Dica tom="aviso" testid={`turma-somente-leitura-${t.id}`}>
                  Você pode ver esta turma{t.criada_por ? ` (criada por ${t.criada_por})` : ''}, mas não
                  pode editar nem excluir: só quem criou, o responsável pela equipe ou o professor da
                  turma pode editar — e só quem criou ou o responsável pode excluir.
                </Dica>
              )}
              {t.pode_editar !== false && t.pode_apagar === false && (
                <Dica tom="aviso" testid={`turma-sem-excluir-${t.id}`}>
                  Você pode editar esta turma, mas não excluir
                  {t.criada_por ? ` (criada por ${t.criada_por})` : ''}: só quem criou ou o
                  responsável pela equipe pode excluir.
                </Dica>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">
              {editando ? `Editar turma — ${editando.name}` : 'Nova turma'}
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-[#A1A1AA]"><span className="text-[#D4AF37]">*</span> campos obrigatórios</p>

          <div className="space-y-3">
            {/* A turma é SEMPRE de uma equipe (é ela que dá a visibilidade ao
                professor). Por isso o campo é obrigatório. */}
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-equipe">Equipe da turma *</Label>
              {equipes.length === 0 ? (
                <p className="text-micro text-amber-400" data-testid="turma-sem-equipe">
                  Você ainda não responde por nenhuma equipe — a turma precisa de uma equipe.
                  Peça ao administrador para vincular você a uma equipe.
                </p>
              ) : (
                <Select
                  value={form.team_id || 'none'}
                  onValueChange={(v) => {
                    setErros((prev) => ({ ...prev, team_id: undefined }));
                    setForm({ ...form, team_id: v === 'none' ? '' : v });
                  }}
                >
                  <SelectTrigger id="turma-equipe" data-testid="turma-equipe" aria-invalid={!!erros.team_id} className="bg-[#1A1A1A] border-[#27272A] text-white">
                    <SelectValue placeholder="Selecione a equipe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                    {equipes.map((eq) => (
                      <SelectItem key={eq.id} value={eq.id} className="text-white">{eq.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {erros.team_id && (
                <p role="alert" className="text-xs text-red-400 mt-1">{erros.team_id}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-nome">Nome da turma *</Label>
                <Input value={form.name}
                       onChange={(e) => {
                         setErros((prev) => ({ ...prev, name: undefined }));
                         setForm({ ...form, name: e.target.value });
                       }}
                       placeholder="Ex: Adulto Avançado" id="turma-nome" data-testid="turma-nome"
                       aria-invalid={!!erros.name}
                       className="bg-[#1A1A1A] border-[#27272A] text-white" />
                {erros.name && (
                  <p role="alert" className="text-xs text-red-400 mt-1">{erros.name}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-nivel">Nível (opcional)</Label>
                <Input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
                       placeholder="Ex: Iniciante / Avançado" id="turma-nivel" data-testid="turma-nivel"
                       className="bg-[#1A1A1A] border-[#27272A] text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-xs">Dias da semana</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="turma-dias">
                {DIAS_SEMANA.map((d) => {
                  const marcado = form.weekdays.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        weekdays: marcado
                          ? form.weekdays.filter((x) => x !== d.value)
                          : [...form.weekdays, d.value].sort((a, b) => a - b),
                      })}
                      data-testid={`turma-dia-${d.value}`}
                      className={`px-2.5 py-1 rounded text-xs border ${
                        marcado
                          ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold'
                          : 'border-[#27272A] text-[#A1A1AA] hover:text-white'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-inicio">Início</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                       id="turma-inicio" data-testid="turma-inicio" className="bg-[#1A1A1A] border-[#27272A] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-fim">Fim</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                       id="turma-fim" data-testid="turma-fim" className="bg-[#1A1A1A] border-[#27272A] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-valor">Mensalidade (R$)</Label>
                <Input type="number" step="0.01" value={form.default_amount}
                       onChange={(e) => setForm({ ...form, default_amount: e.target.value })}
                       placeholder="150,00" id="turma-valor" data-testid="turma-valor"
                       className="bg-[#1A1A1A] border-[#27272A] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-xs" htmlFor="turma-vencimento">Vence no dia</Label>
                <Input type="number" min="1" max="28" value={form.due_day}
                       onChange={(e) => setForm({ ...form, due_day: e.target.value })}
                       id="turma-vencimento" data-testid="turma-vencimento"
                       className="bg-[#1A1A1A] border-[#27272A] text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-xs">
                Professores da turma <span className="text-[#71717A]">(só os da equipe)</span>
              </Label>
              {professores.length === 0 ? (
                <p className="text-micro text-[#71717A]" data-testid="turma-sem-professores">
                  Nenhum professor vinculado a esta equipe. Vincule o professor na tela de
                  Equipes para ele aparecer aqui.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="turma-professores">
                  {professores.map((p) => {
                    const marcado = form.professor_ids.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          professor_ids: marcado
                            ? form.professor_ids.filter((x) => x !== p.id)
                            : [...form.professor_ids, p.id],
                        })}
                        data-testid={`turma-professor-${p.id}`}
                        className={`px-2.5 py-1 rounded text-xs border ${
                          marcado ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold'
                                  : 'border-[#27272A] text-[#A1A1AA] hover:text-white'
                        }`}
                      >
                        {p.name}
                        {p.role === 'team' ? ' (equipe)' : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-[#A1A1AA] text-xs">
                  Alunos da turma ({form.students.length} selecionado(s))
                </Label>
                <div className="relative w-56">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                  <Input value={busca} onChange={(e) => setBusca(e.target.value)}
                         placeholder="Buscar aluno..." data-testid="turma-buscar-aluno"
                         className="pl-7 h-8 bg-[#1A1A1A] border-[#27272A] text-white text-xs" />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto rounded border border-[#27272A] divide-y divide-[#27272A]"
                   data-testid="turma-lista-alunos">
                {alunosFiltrados.length === 0 ? (
                  <p className="p-3 text-micro text-[#71717A]">Nenhum aluno encontrado.</p>
                ) : alunosFiltrados.map((a) => {
                  const escolhido = form.students.find((s) => s.athlete_id === a.id);
                  return (
                    <div key={a.id} className="flex items-center gap-2 p-2">
                      <input
                        type="checkbox"
                        checked={!!escolhido}
                        onChange={() => alternarAluno(a.id)}
                        data-testid={`turma-aluno-${a.id}`}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="flex-1 text-xs text-white truncate">
                        {a.name} <span className="text-[#71717A]">· {a.belt}</span>
                      </span>
                      {escolhido && (
                        <Input
                          type="number" step="0.01"
                          value={escolhido.amount}
                          onChange={(e) => mudarValorAluno(a.id, e.target.value)}
                          placeholder="valor da turma"
                          data-testid={`turma-aluno-valor-${a.id}`}
                          className="w-32 h-7 bg-[#1A1A1A] border-[#27272A] text-white text-xs"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <Dica icone={false}>
                O valor ao lado do aluno é opcional: vazio usa o valor padrão da turma
                ({formatarValor(form.default_amount === '' ? null : Number(form.default_amount))}).
              </Dica>
            </div>

            <BarraAcoes contexto="cartao">
              <Button variant="outline" onClick={() => setAberto(false)}
                      className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
              <Button onClick={salvar} disabled={salvando} data-testid="salvar-turma"
                      className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar turma'}
              </Button>
            </BarraAcoes>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
